// 此文件为Mach初始化代码，依赖的方法要保证不能再使用Mach
import {
  H5_APPNAME, isAndroid, isH5, isIOS
} from '@m2h/common/constants';
import { getBowserInfos, getBowserDeviceInfo } from '@m2h/common/utils/bowserUtil';
import { getPlatform, isProd } from '@m2h/common/utils/common';
import cookie from 'js-cookie';
import {
  createMachEventManager, EventHandlerWrapper,
} from './eventRegistry';

type ReadyCallback = ((status: number) => void) | null;

// 扩展Window类型
declare global {
  interface Window {
    machEventHandlers?: { [key: string]: (Function | EventHandlerWrapper)[]; };
    machEventHandlerRegistry?: Map<string, Set<string>>;
  }
}

// mach事件，都放到顶层来响应。
// 修复iframe中绑定的事件在页面关闭时未正确清理的问题
// frame中绑定的事件，在页面关闭时，不一定都能正确off、事件处理器残留
// 重复判断机制失效：通过函数字符串比较判重，但iframe关闭后函数引用可能失效
// 内存泄漏风险：未清理的事件处理器会导致内存泄漏
if (!window.top.machEventHandlers) {
  window.top.machEventHandlers = {};
}
if (!window.top.machEventHandlerRegistry) {
  window.top.machEventHandlerRegistry = new Map(); // 用于跟踪事件处理器的注册信息
}
const NativeModuleMap = new Map();
const __event_handlers__ = window.top.machEventHandlers;
const __handler_registry__ = window.top.machEventHandlerRegistry;

// 为当前窗口生成唯一标识符
const WINDOW_ID = `window_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
const eventManager = createMachEventManager({
  handlers: __event_handlers__,
  registry: __handler_registry__,
}, WINDOW_ID, window);
//@ts-ignore
const __MachPro_SafeArea = window.__safeArea || {
  right: 0,
  top: 0, // 待定
  left: 0,
  bottom: 16, // h5底部默认值16
};

const __userAgent = navigator.userAgent;

// 清理已失效的事件处理器
function _cleanupInvalidHandlers(event?: string) {
  eventManager.cleanupInvalid(event);
}

function _registerEvent(event: string, handler: any, options?: { persist?: boolean }) {
  return eventManager.on(event, handler, options);
}

function _triggerEvent(event: string, data: any) {
  eventManager.trigger(event, data);
}

function _offEvent_(event, handler) {
  eventManager.off(event, handler);
}

function styleTransform(num: number, useNumber: boolean = false) {
  try {
    //@ts-ignore
    const { defaultUnit, transform } = MACH_PROJECT_CONFIG.pxTransform;
    let unit = defaultUnit;

    if (transform && transform[unit]) {
      num = (transform[unit].ratio || 1) * num;
      unit = transform[unit].unit || unit;
    }
    if (useNumber) return num;
    return num + unit;
  } catch (error) {
    return num;
  }
}

function _urlParamToObj(extraParam: string = '') {
  const obj = {};
  let params = '';
  try {
    if (window.location.search) {
      params = window.location.search.split('?')[1];
    } else {
      params = window.location.href.split('?')[1];
    }
    if (params) {
      params
        .concat(extraParam)
        .split('&')
        .map((item) => {
          if (item) return (obj[item.split('=')[0]] = decodeURIComponent(item.split('=')[1]));
        });
    }
    return obj;
  } catch (error) {
    return {};
  }
}

const __GlobalSchemeParams: {
  uuid?: string;
  wm_appversion?: string;
  version_name?: string;
} = _urlParamToObj();

function __listenerUrl() {
  // @ts-ignore
  if (window.__$router) {
    // @ts-ignore
    window.__$router.history.listen((param) => {
      const extra = param.location.search.split('?')[1];
      if (extra) {
        Mach.env.schemeParams = _urlParamToObj(`&${extra}`);
      } else Mach.env.schemeParams = _urlParamToObj();
    });
  }
}

function __generateUuid() {
  console.log('uuid __generateUuid');
  // TODO， 临时调试-写死
  return '0000000000000A397FBE50883486F82B6709AF271CAF5A172959460031976406';
  // 0000000000000E6E7D6393C3549B5926F9E1728118EAAA169481323472760000
  // eslint-disable-next-line no-unreachable
  let timestamp = new Date().getTime();
  let perforNow = (typeof performance !== 'undefined' && performance.now && performance.now() * 1000) || 0;
  return 'xxxxxxxxxxxx4xxxyxxxxxxxxxxxxxxxxxxxxxxxxxxx4xxxyxxxxxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    let random = Math.random() * 16;
    if (timestamp > 0) {
      random = (timestamp + random) % 16 | 0;
      timestamp = Math.floor(timestamp / 16);
    } else {
      random = (perforNow + random) % 16 | 0;
      perforNow = Math.floor(perforNow / 16);
    }
    return (c === 'x' ? random : (random & 0x3) | 0x8).toString(16).toUpperCase();
  });
}

// 获取灵犀生成的uuid, 获取不到则兜底写死值
function getLxUuid() {
  return sessionStorage.getItem('_lxsdk_cuid')?.replace(/"/g, '')
    || cookie.get('_lxsdk_cuid')
    || localStorage.getItem('__lxsdk__lxsdk_cuid')?.replace(/"/g, '')
    || '';
}

function __getUuid() {
  // TODO， 临时调试-写死
  return getLxUuid() || '0000000000000A397FBE50883486F82B6709AF271CAF5A172959460031976406';
  // return '0000000000000A397FBE50883486F82B6709AF271CAF5A172959460031976406';
  // eslint-disable-next-line no-unreachable
  try {
    let uuid = __GlobalSchemeParams.uuid || window.localStorage.getItem('uuid') || '';
    if (!uuid) {
      uuid = __generateUuid();
      window.localStorage.setItem('uuid', uuid);
    }
    return uuid;
  } catch (e) {
    console.log(e);
  }
  return '';
}

function __getAppVersion() {
  // 站内,外卖小程序当中打开的场景，通过地址栏获取同参, 外卖：wm_appversion  团：version_name
  return __GlobalSchemeParams.wm_appversion || __GlobalSchemeParams.version_name || '2.11.200';
}

/**
 * 获取操作系统名称 (使用 Bowser 库 + 降级方案)
 * 支持: iOS, Android, Windows, macOS, Linux, ChromeOS, Ubuntu, Fedora, Debian 等
 * 返回值: 'ios' | 'android' | 'windows' | 'macos' | 'linux' | 'chromeos' | 'ubuntu' | 'fedora' | 'debian' | 'pc'
 */
function __getOsName(): string {
  const { os } = getBowserDeviceInfo();
  return os;
}

function __getOsVersion() {
  const ua = navigator.userAgent;
  let osVersion = '';

  try {
    if (/(iPhone|iPad|iPod|iOS)/i.test(ua)) {
      // iOS版本获取
      // 匹配 "OS 15_1" 或 "OS 15.1" 或 "Version/15.1"
      const matches = ua.match(/OS\s+([\d._]+)/) || ua.match(/Version\/([\d.]+)/);
      if (matches && matches[1]) {
        osVersion = matches[1].replace(/_/g, '.');
      }
    } else if (/(Android)/i.test(ua)) {
      // Android版本获取
      // 匹配 "Android 12" 或 "Android 12.0"
      const matches = ua.match(/Android\s+([0-9.]+)/);
      if (matches && matches[1]) {
        osVersion = matches[1];
      }
    } else if (/Windows/i.test(ua)) {
      // Windows版本获取
      if (ua.indexOf('Windows NT 10.0') !== -1) osVersion = '10';
      else if (ua.indexOf('Windows NT 6.3') !== -1) osVersion = '8.1';
      else if (ua.indexOf('Windows NT 6.2') !== -1) osVersion = '8';
      else if (ua.indexOf('Windows NT 6.1') !== -1) osVersion = '7';
      else if (ua.indexOf('Windows NT 6.0') !== -1) osVersion = 'Vista';
      else if (ua.indexOf('Windows NT 5.1') !== -1) osVersion = 'XP';
    } else if (/Mac OS X/i.test(ua)) {
      // macOS版本获取
      const matches = ua.match(/Mac OS X\s*([0-9_]+)/i);
      if (matches && matches[1]) {
        osVersion = matches[1].replace(/_/g, '.');
      }
    } else if (/Linux/i.test(ua)) {
      // Linux版本
      osVersion = 'Linux';
    }

    // 清理版本号中的多余小数点
    osVersion = osVersion.replace(/\.+$/, '');

    return osVersion || '';
  } catch (e) {
    console.warn('获取操作系统版本失败:', e);
    return '';
  }
}

function __getOsInfo() {
  const ua = navigator.userAgent;
  let osVersion = '';

  try {
    if (/(iPhone|iPad|iPod|iOS)/i.test(ua)) {
      // iOS版本获取
      // 匹配 "OS 15_1" 或 "OS 15.1" 或 "Version/15.1"
      const matches = ua.match(/OS\s+([\d._]+)/) || ua.match(/Version\/([\d.]+)/);
      if (matches && matches[1]) {
        osVersion = matches[1].replace(/_/g, '.');
      }
    } else if (/(Android)/i.test(ua)) {
      // Android版本获取
      // 匹配 "Android 12" 或 "Android 12.0"
      const matches = ua.match(/Android\s+([0-9.]+)/);
      if (matches && matches[1]) {
        osVersion = matches[1];
      }
    } else if (/Windows/i.test(ua)) {
      // Windows版本获取
      if (ua.indexOf('Windows NT 10.0') !== -1) osVersion = '10';
      else if (ua.indexOf('Windows NT 6.3') !== -1) osVersion = '8.1';
      else if (ua.indexOf('Windows NT 6.2') !== -1) osVersion = '8';
      else if (ua.indexOf('Windows NT 6.1') !== -1) osVersion = '7';
      else if (ua.indexOf('Windows NT 6.0') !== -1) osVersion = 'Vista';
      else if (ua.indexOf('Windows NT 5.1') !== -1) osVersion = 'XP';
    } else if (/Mac OS X/i.test(ua)) {
      // macOS版本获取
      const matches = ua.match(/Mac OS X\s*([0-9_]+)/i);
      if (matches && matches[1]) {
        osVersion = matches[1].replace(/_/g, '.');
      }
    } else if (/Linux/i.test(ua)) {
      // Linux版本
      osVersion = 'Linux';
    }

    // 清理版本号中的多余小数点
    osVersion = osVersion.replace(/\.+$/, '');

    return osVersion || '';
  } catch (e) {
    console.warn('获取操作系统版本失败:', e);
    return '';
  }
}

function __getWeixinAppName() {
  //@ts-ignore
  return window.__wxjs_environment ? 'Weixin' : H5_APPNAME;
}

/**
 * 环境变量：https://km.sankuai.com/page/746256726
 */
const getInitMachConf = () => ({
  env: {
    __ready: null as ReadyCallback,
    registerReady(callback: (status: number) => void) {
      if (typeof callback === 'function') {
        this.__ready = callback;
      }
    },
    networkTimestamp() {
      return parseInt((Date.now() / 1000).toString());
    },
    // -----相对固定不变的配置
    statusBarHeight: __MachPro_SafeArea.top,
    navigationBarHeight: 44, // 导航条高度。 0？
    // scale: 3,
    isAppInBackground: false,
    runInJSThread: false, // machpro通知native使用js子线程，h5不用考虑子线程问题
    isFullScreen: true,
    // isFullScreen:
    //   window
    //   && navigator.userAgent.indexOf('iPhone') > -1
    //   && window.screen.height >= 812
    //   && window.devicePixelRatio >= 2,
    softMenuBarHeight: 0,
    isPageVisible: true,
    safeAreaInsets: __MachPro_SafeArea,
    isDebug: !isProd(false),
    // isDebug: false,
    partner: 85204,
    isH5,
    isIOS, // H5模式，不通过UA判断，mach代码对ios/android的图书处理容易有样式问题
    isAndroid,
    // isIOS: !!__userAgent.match(/\(i[^;]+;( U;)? CPU.+Mac OS X/),
    // isAndroid: __userAgent.indexOf('Android') > -1 || __userAgent.indexOf('Adr') > -1,
    diTingPageId: '', // 谛听。 需谛H5支持:https://km.sankuai.com/collabpage/378730769
    // 以上为H5固定配置
    // -----路由、页面差异的配置
    bundleName: (window.MACH_PROJECT_CONFIG || { bundleName: 'mach_pro_sailor_c_shop_golden' }).bundleName,
    bundleVersion: '0.0.1',
    schemeParams: __GlobalSchemeParams,
    // -----以上为路由、页面差异的配置
    // -----运行环境实时计算的配置
    screenWidth: window.innerWidth,
    screenHeight: window.innerHeight,
    windowWidth: window.innerWidth,
    windowHeight: window.innerHeight,
    scale: window.devicePixelRatio,
    isWXH5: !!window.__wxjs_environment,
    scheme: window.location.href,
    platform: getPlatform(), // 通参说明https://km.sankuai.com/collabpage/1508513607
    appName: __getWeixinAppName(),
    version: __getAppVersion(),
    osName: __getOsName(),
    uuid: __getUuid(),
    ...getBowserInfos(),
    // osVersion: __getOsVersion(),
    // deviceModel: 'iPhone 15 Pro Max',
    // -----以上为运行环境实时计算的配置
  },
  isWx: false,
  data: {},
});

// 实时获取的参数值，可覆盖缓存中的值
const getRealTimeMachConf = () => ({
  env: {
    screenWidth: window.innerWidth,
    screenHeight: window.innerHeight,
    windowWidth: window.innerWidth,
    windowHeight: window.innerHeight,
    scale: window.devicePixelRatio,
    isWXH5: !!window.__wxjs_environment,
    scheme: window.location.href,
    platform: getPlatform(), // 通参说明https://km.sankuai.com/collabpage/1508513607
    // 以上为H5固定配置
    appName: __getWeixinAppName(),
    version: __getAppVersion(),
    osName: __getOsName(),
    ...getBowserInfos(),
    uuid: __getUuid(),
  },
  data: {},
});

const Mach = {
  ...getInitMachConf(),
  exportModule() {},
  registerModule: (moduleName, customModule) => {
    NativeModuleMap.set(moduleName, customModule);
  },
  /**
   * 获取内置模块
   * @param moduleName 内置模块名称
   * @returns 内置模块实例
   */
  requireModule: (moduleName) => NativeModuleMap.get(moduleName),
  requireBundleAsync() {},
  on: _registerEvent,
  off: _offEvent_,
  subscribeEvent: _registerEvent,
  sendEvent: _triggerEvent,
  receiveEvent: _triggerEvent,
  styleTransform,
};

async function __KNBPromise(key: string) {
  return new Promise((reslove, reject) => {
    //@ts-ignore
    if (KNB[key]) {
      //@ts-ignore
      KNB[key]({
        success(info) {
          reslove(info);
        },
        fail(err) {
          console.log(`knb-${key}-error`, err);
          reslove({});
        },
      });
    }
  });
}

function __getAppName(appId: number) {
  if ([10110, 10111, 10120, 10121].includes(appId)) {
    return 'group';
  }
  if ([10220, 10221, 10210, 10211].includes(appId)) {
    return 'Nova';
  }
  if ([10310, 10311, 10320, 10321].includes(appId)) {
    return 'waimai';
  }
  if ([10710, 10711, 10720, 10721].includes(appId)) {
    return 'waimai';
  }
  return H5_APPNAME;
}

// 页面卸载时清理当前窗口的事件处理器
function _cleanupCurrentWindowHandlers() {
  eventManager.cleanupCurrentWindow();
}

// 新路由挂载时清理已卸载路由注册的监听器（SPA 僵尸监听自动失效）。
function _cleanupStaleRouteHandlers(currentRoutePath: string) {
  return eventManager.cleanupStaleRouteHandlers(currentRoutePath);
}

async function initMach() {
  try {
    // const uaInfo: any = await __KNBPromise('getAppInfo');
    // const deviceInfo: any = await __KNBPromise('getDeviceInfo');
    // const { os = '', osVersion = '' } = deviceInfo;
    // __listenerUrl();
    Mach.env = Object.assign(Mach.env, {
      isReady: true,
      // appName: __getAppName(Number(uaInfo.appid)),
      // version: uaInfo.version,
      // osName: os,
      // osVersion,
      // isIOS: os.toLowerCase() === 'ios',
      // isAndroid: os.toLowerCase() === 'android',
      // deviceModel: deviceInfo.model,
      // uaInfo,
      // deviceInfo,
    });
  } catch (e) {
  }
  return Mach.env;
}

export {
  NativeModuleMap,
  getInitMachConf,
  getRealTimeMachConf,
  _cleanupInvalidHandlers,
  _cleanupCurrentWindowHandlers,
  _cleanupStaleRouteHandlers,
  initMach,
};

export default Mach;
